import { useCallback, useEffect, useReducer, useState } from "react";

import type StorageClass from "./StorageClass";
import { createStorage } from "./StorageClass";
import { useStorageClient } from "./StorageClientProvider";
import type { StorageClient } from "./types";

const usePersistedState = <State>(
  storageKey: string,
  initialValue: State | undefined,
  storage: StorageClass,
) => {
  const [value, setValue] = useState(() =>
    storage.has(storageKey) ? storage.get(storageKey) : initialValue,
  );

  useEffect(() => {
    if (value !== undefined) {
      storage.set(storageKey, value);
    }
  }, [value, storageKey, storage]);

  return [value, setValue];
};

type Reducer<State> = (state: State, action: unknown) => State;

const usePersistedReducer = <State>(
  reducer: Reducer<State>,
  initialState: State,
  storageKey: string,
  storage: StorageClass,
) => {
  const reducerWithStorage = useCallback(
    (state: State, action: unknown) => {
      const newState = reducer(state, action);
      storage.set(storageKey, newState);
      return newState;
    },
    [storage, reducer, storageKey],
  );

  return useReducer(reducerWithStorage, initialState, (initial) => {
    return storage.has(storageKey) ? storage.get(storageKey) : initial;
  });
};

export const usePersistedSessionState = <State>(
  storageKey: string,
  initialValue?: State,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedState(
    storageKey,
    initialValue,
    createStorage(storageClient.get("sessionStorage")),
  );
};

export const usePersistedScopedSessionState = <State>(
  storageKey: string,
  initialValue?: State,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedState(
    storageKey,
    initialValue,
    createStorage(storageClient.get("sessionStorage"), "fe-shell:"),
  );
};

export const usePersistedLocalState = <State>(
  storageKey: string,
  initialValue?: State,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedState(
    storageKey,
    initialValue,
    createStorage(storageClient.get("localStorage")),
  );
};

export const usePersistedScopedLocalState = <State>(
  storageKey: string,
  initialValue?: State,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedState(
    storageKey,
    initialValue,
    createStorage(storageClient.get("localStorage"), "fe-shell:"),
  );
};

export const usePersistedSessionReducer = <State>(
  reducer: Reducer<State>,
  initialState: State,
  storageKey: string,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedReducer(
    reducer,
    initialState,
    storageKey,
    createStorage(storageClient.get("sessionStorage")),
  );
};

export const usePersistedLocalReducer = <State>(
  reducer: Reducer<State>,
  initialState: State,
  storageKey: string,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedReducer(
    reducer,
    initialState,
    storageKey,
    createStorage(storageClient.get("localStorage")),
  );
};

export const usePersistedScopedSessionReducer = <State>(
  reducer: Reducer<State>,
  initialState: State,
  storageKey: string,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedReducer(
    reducer,
    initialState,
    storageKey,
    createStorage(storageClient.get("sessionStorage"), "fe-shell:"),
  );
};

export const usePersistedScopedLocalReducer = <State>(
  reducer: Reducer<State>,
  initialState: State,
  storageKey: string,
) => {
  const storageClient = useStorageClient() as StorageClient;
  return usePersistedReducer(
    reducer,
    initialState,
    storageKey,
    createStorage(storageClient.get("localStorage"), "fe-shell:"),
  );
};
